import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useRefresh,
  useUpdate,
} from "ra-core";
import { format, formatDistanceToNowStrict, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "react-router";
import { Check, Flame, MoreHorizontal, StickyNote } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { getContactDisplayName } from "../contacts/contactModel";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import type { Contact, Subscription } from "../types";

// Sprint 4 + Sprint 10.x — Spec Théo 2026-05-13 + 2026-05-15 :
// "Tous les nouveaux inscrits plateforme qui n'ont pas payé d'abonnement,
//  bouton Fait (note auto en timeline + ligne disparaît avec undo 5s)."
//
// Filtre serveur :
//   - population = 'plateforme'
//   - archived_at IS NULL
// Filtres client-side (pas de view dédiée) :
//   - last_relance_at IS NULL OR last_relance_at < now-7d (cooldown)
//   - company_id NOT IN (companies avec sub active/trialing)
//   - id NOT IN (dismissedIds) — disparition immédiate post-clic Fait
// Tri : first_seen ASC (les plus anciens d'abord — un J+3 non relancé doit
// remonter devant un J+0 fraîchement inscrit, sinon le commercial perd les
// vieilles relances en bas du tas).
// Cap : 50 lignes affichées avec compteur "+X autres" si dépassement.

const DISPLAY_CAP = 50;

export const MyDayContactsToRelance = () => {
  const sevenDaysAgo = subDays(new Date(), 7).toISOString();
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  const dismiss = useCallback((id: number) => {
    setDismissedIds((s) => {
      const n = new Set(s);
      n.add(id);
      return n;
    });
  }, []);

  const undismiss = useCallback((id: number) => {
    setDismissedIds((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  }, []);

  // Subscriptions actives → on récupère les company_ids à exclure.
  const { data: activeSubs } = useGetList<Subscription>("subscriptions", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "company_id", order: "ASC" },
    filter: { "status@in": "(active,trialing)" },
  });
  const paidCompanyIds = useMemo(
    () => new Set((activeSubs ?? []).map((s) => s.company_id)),
    [activeSubs],
  );

  const { data, isPending } = useGetList<Contact>("contacts_summary", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_seen", order: "ASC" },
    filter: {
      population: "plateforme",
      "archived_at@is": null,
    },
  });

  const eligible = useMemo(() => {
    const cutoff = new Date(sevenDaysAgo);
    return (data ?? [])
      .filter((c) => !c.last_relance_at || new Date(c.last_relance_at) < cutoff)
      .filter((c) => !c.company_id || !paidCompanyIds.has(c.company_id))
      .filter((c) => !dismissedIds.has(c.id as number));
  }, [data, paidCompanyIds, sevenDaysAgo, dismissedIds]);

  const filtered = eligible.slice(0, DISPLAY_CAP);
  const hiddenCount = eligible.length - filtered.length;

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame className="size-4 text-fabrik-orange" />
            <p className="text-[13px] font-semibold text-foreground">
              Contacts à relancer
            </p>
          </div>
          {eligible.length ? (
            <Badge variant="secondary" className="text-[11px]">
              {eligible.length}
            </Badge>
          ) : null}
        </div>

        {isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : filtered.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground">
            Personne à relancer pour l'instant.
          </p>
        ) : (
          <>
            <ul className="flex flex-col divide-y divide-border/40">
              {filtered.map((contact) => (
                <RelanceRow
                  key={contact.id}
                  contact={contact}
                  onDismiss={dismiss}
                  onUndoDismiss={undismiss}
                />
              ))}
            </ul>
            {hiddenCount > 0 && (
              <p className="text-[11.5px] text-muted-foreground mt-2 text-center">
                + {hiddenCount} autres à relancer (affine ta journée pour les
                voir)
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

function RelanceRow({
  contact,
  onDismiss,
  onUndoDismiss,
}: {
  contact: Contact;
  onDismiss: (id: number) => void;
  onUndoDismiss: (id: number) => void;
}) {
  const { identity } = useGetIdentity();
  const [createNote] = useCreate();
  const [deleteNote] = useDelete();
  const [update] = useUpdate();
  const refresh = useRefresh();
  const [isMarking, setIsMarking] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // first_seen = date d'inscription réelle (immutable).
  // Bug Théo 2026-05-15 : on utilisait last_seen mais ce dernier est mis
  // à jour par le trigger handle_contact_note_created_or_updated dès qu'une
  // note (y compris la note auto "Relance faite") est créée → affichage
  // trompeur "inscrit il y a 3 minutes" pour des contacts vraiment anciens.
  const rel = contact.first_seen
    ? formatDistanceToNowStrict(new Date(contact.first_seen), {
        locale: fr,
        addSuffix: true,
      })
    : "—";

  // handleDone reçoit désormais le texte de note final (préfixe auto +
  // éventuel enrichissement saisi par l'utilisateur dans la popup). Le
  // déclencheur n'est plus la checkbox elle-même mais le bouton "Marquer
  // comme fait" de RelanceCompletionDialog ouvert depuis la checkbox ou
  // le dropdown.
  const handleDone = async (noteText: string) => {
    if (isMarking) return;
    setIsMarking(true);

    const contactId = contact.id as number;
    const previousLastRelanceAt = contact.last_relance_at ?? null;

    // Disparition immédiate côté UI — l'état parent filtre la ligne hors de
    // la liste avant même que la mutation revienne.
    onDismiss(contactId);

    let noteId: number | string | null = null;
    try {
      noteId = await new Promise<number | string>((resolve, reject) =>
        createNote(
          "contact_notes",
          {
            data: {
              contact_id: contactId,
              text: noteText,
              sales_id: identity?.id,
              date: new Date().toISOString(),
            },
          },
          {
            onSuccess: (res: any) => resolve(res?.id),
            onError: (err) => reject(err),
          },
        ),
      );
    } catch {
      onUndoDismiss(contactId);
      toast.error("Impossible d'archiver la relance");
      setIsMarking(false);
      return;
    }

    try {
      await new Promise<void>((resolve, reject) =>
        update(
          "contacts",
          {
            id: contactId,
            data: { last_relance_at: new Date().toISOString() },
            previousData: contact,
          },
          {
            mutationMode: "optimistic",
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        ),
      );

      toast.success("Relance marquée faite", {
        duration: 5000,
        action: {
          label: "Annuler",
          onClick: async () => {
            // Undo : restaurer last_relance_at + supprimer la note.
            // Trigger update_contact_status_on_activity se relance sur
            // DELETE de la note → contact.status se recalcule auto.
            try {
              if (noteId !== null) {
                await new Promise<void>((resolve, reject) =>
                  deleteNote(
                    "contact_notes",
                    { id: noteId as any, previousData: { id: noteId } as any },
                    {
                      onSuccess: () => resolve(),
                      onError: (err) => reject(err),
                    },
                  ),
                );
              }
              await new Promise<void>((resolve, reject) =>
                update(
                  "contacts",
                  {
                    id: contactId,
                    data: { last_relance_at: previousLastRelanceAt },
                    previousData: {
                      ...contact,
                      last_relance_at: new Date().toISOString(),
                    },
                  },
                  {
                    onSuccess: () => resolve(),
                    onError: (err) => reject(err),
                  },
                ),
              );
              onUndoDismiss(contactId);
              refresh();
              toast.info("Relance annulée");
            } catch {
              toast.error("Impossible d'annuler la relance", {
                description: "La note reste en timeline. Annule à la main.",
              });
            }
          },
        },
      });
      // On refresh quand même pour synchroniser le cache contacts_summary
      // (filter cooldown re-évalué côté backend).
      refresh();
    } catch {
      // La note est créée mais l'update a échoué → on garde la dismiss
      // visuelle mais on prévient l'utilisateur.
      toast.error("Note archivée mais cooldown non posé", {
        description:
          "La trace est dans la timeline. Le contact réapparaîtra à la prochaine session.",
      });
      setIsMarking(false);
    }
  };

  return (
    <>
      <li className="py-2 first:pt-0 last:pb-0">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={false}
            onCheckedChange={(c) => {
              if (c === true && !isMarking) setConfirmOpen(true);
            }}
            disabled={isMarking}
            aria-label={`Marquer relance faite pour ${contact.first_name ?? ""}`}
            className="shrink-0"
          />
          <Link
            to={`/contacts/${contact.id}/show`}
            className="flex items-center justify-between gap-3 min-w-0 flex-1 hover:opacity-80"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {getContactDisplayName(contact)}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {contact.company_name ? `${contact.company_name} · ` : ""}
                inscrit {rel}
              </p>
            </div>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-fabrik-text-3 hover:text-foreground"
                aria-label="Plus d'actions"
                onClick={(e) => e.preventDefault()}
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setNoteOpen(true)}>
                <StickyNote className="size-3.5" />
                Ajouter une note
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  if (!isMarking) setConfirmOpen(true);
                }}
              >
                <Check className="size-3.5" />
                Marquer relance faite
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </li>
      <NoteCreateSheet
        open={noteOpen}
        onOpenChange={setNoteOpen}
        contact_id={contact.id}
      />
      <RelanceCompletionDialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (!isMarking) setConfirmOpen(o);
        }}
        contactName={getContactDisplayName(contact)}
        onConfirm={(noteText) => {
          setConfirmOpen(false);
          void handleDone(noteText);
        }}
        submitting={isMarking}
      />
    </>
  );
}

// Sprint 10.x — Popup confirmation Théo 2026-05-15 : avant de marquer la
// relance faite, on demande une note enrichie. La textarea est pré-remplie
// avec "✓ Relance faite : [date]\n\n" — le curseur est positionné après les
// 2 retours pour que Théo puisse ajouter du contexte. Le bouton "Marquer
// comme fait" reste toujours actif : si Théo ne tape rien, la note auto
// est sauvegardée (préserve le flow rapide actuel).
function RelanceCompletionDialog({
  open,
  onOpenChange,
  contactName,
  onConfirm,
  submitting,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  contactName: string;
  onConfirm: (noteText: string) => void;
  submitting: boolean;
}) {
  const prefix = useMemo(
    () =>
      `✓ Relance faite : ${format(new Date(), "d MMMM yyyy", { locale: fr })}\n\n`,
    // recalculer le préfixe à chaque ouverture (date du jour)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );
  const [text, setText] = useState(prefix);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      setText(prefix);
      // Positionner le curseur après le préfixe une fois la textarea montée.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(prefix.length, prefix.length);
        }
      });
    }
  }, [open, prefix]);

  const submit = () => {
    const trimmed = text.trim();
    onConfirm(trimmed.length > 0 ? trimmed : prefix.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Marquer la relance faite</DialogTitle>
          <DialogDescription>
            Optionnel : ajoute un mot pour enrichir la timeline de{" "}
            <span className="font-medium text-foreground">{contactName}</span>{" "}
            (signal, contexte, prochaine étape…).
          </DialogDescription>
        </DialogHeader>
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          className="font-mono text-[13px]"
          disabled={submitting}
          placeholder="✓ Relance faite : …"
        />
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Annuler
          </Button>
          <Button type="button" onClick={submit} disabled={submitting}>
            {submitting ? "Enregistrement…" : "Marquer comme fait"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
